import Button from "components/button";
import Pagination from "components/pagination";
import { Breadcrumbs } from "components/breadcrumbs";
import { AccountCard, AccountTable, Filter } from "./components";
import { Link } from "react-router-dom";
import { PlusIcon } from '@heroicons/react/solid'
import { FullScreenLoading } from "components/fullScreenLoading";
import { useMemo } from "react";
import { useTable, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { isHaveActionPermission } from "utills/helpers";

export const OrgAccountList: React.FC = () => {

  const {
    tableQueryResult,
    filters,
    setFilters,
  } = useTable<any>({
    resource: "organization/account",
    syncWithLocation: true,
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "accounts",
    },
  });


  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("account_management", "organization", "edit"))
      return;
    if (window.confirm(`Are you sure you want to ${isActive ? "deactivate" : "activate"} this Account?`)) {
      updateMutation({
        resource: "organization/account",
        id,
        meta: {
          URLSuffix: `active/${!isActive}`,
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : [],
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      organization_id: logicalFilters.find((item) => item.field === "organization_id")?.value || "",
      role_id: logicalFilters.find((item) => item.field === "role_id")?.value || "",
    };
  }, [filters]);

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }

  if (tableQueryResult.isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission(
          "account_management",
          "organization",
          "create"
        ) && {
          additionalLabel: (
            <Link to={"/organization/accounts/create"}>
              <Button>
                <span className="sr-only">Create New Account</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Create New Account
                </span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter currentFilterValues={currentFilterValues} setFilters={setFilters} />
      </div>
      <div className="box-container">
        {!tableQueryResult.data?.data?.length &&
        !tableQueryResult.isFetching && (
          <div className="text-center">
            No data
          </div>
        )}
        {tableQueryResult?.data?.data && tableQueryResult?.data?.data.length > 0 ? (
          <>     
            <div className="sm:hidden">
              <AccountCard 
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <AccountTable 
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQueryResult?.data?.paging ? (
          <div className="mt-6">
            <Pagination 
              currentPage={tableQueryResult?.data?.paging.current_page}
              totalPage={tableQueryResult?.data?.paging.total_page}
              total={{
                totalItems: tableQueryResult?.data?.paging.total_item || 0,
                total: tableQueryResult?.data?.paging.total
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQueryResult.isFetching && tableQueryResult.isPreviousData) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
};